import { ReactNode } from 'react';

import {
  Icons,
  TableActionButton,
  TableBulkActions,
} from '@cast/design-system';

import { useToggleWorkloadOptimizationChangeDialog } from 'features/cluster/workload-optimization/_hooks/useToggleWorkloadOptimizationChangeDialog';
import {
  OptimizationType,
  PreparedWorkload,
} from 'features/workload-optimization';
import { HpaV2Status } from '../../../../_hooks/useHpaV2Status';

import { useIsConfirmationRequiredOnToggleOptimization } from '../../../../_hooks/permissions/useIsConfirmationRequiredOnToggleOptimization';
import { useWorkloadOptimizationChangeConfirmationDialogs } from '../../../../_hooks/useWorkloadOptimizationChangeConfirmationDialogs';
import { useAssignPolicyDrawer } from '../../_hooks/useAssignPolicyDrawer';

type WorkloadsTableBulkActionsProps = {
  workloads: PreparedWorkload[];
  showHpaV2: boolean;
  isWoopDemoMode: boolean;
  hpaV2Status?: HpaV2Status;
  selectedIds: string[];
  toggleOptimization: (params: { optimizationType: OptimizationType }) => void;
  onDisabled: boolean;
  offDisabled: boolean;
  enableBatchAutoAssignment: () => void;
  canEnableAutoAssignmentCount: boolean;
  enablePredictiveWorkloads: (params: {
    successMsg: ReactNode;
    errorMsg: ReactNode;
  }) => void;
  disablePredictiveWorkloads: (params: {
    successMsg: ReactNode;
    errorMsg: ReactNode;
  }) => void;
  canEnableWorkloadsCount: number;
  canDisableEditableWorkloads: number;
  showPredictiveColumn: boolean;
};

export const WorkloadsTableBulkActions = ({
  workloads,
  showHpaV2,
  isWoopDemoMode,
  hpaV2Status,
  selectedIds,
  toggleOptimization,
  onDisabled,
  offDisabled,
  enableBatchAutoAssignment,
  canEnableAutoAssignmentCount,
  enablePredictiveWorkloads,
  disablePredictiveWorkloads,
  canEnableWorkloadsCount,
  canDisableEditableWorkloads,
  showPredictiveColumn,
}: WorkloadsTableBulkActionsProps) => {
  const { openAssignPolicyDrawer } = useAssignPolicyDrawer();
  const { openOptimizationChangeDialog } =
    useToggleWorkloadOptimizationChangeDialog({ hpaV2Status });
  const { openChangeOptimizationStatusOfMultipleWorkloadsConfirmationDialog } =
    useWorkloadOptimizationChangeConfirmationDialogs();
  const { isConfirmationRequired } =
    useIsConfirmationRequiredOnToggleOptimization();

  return (
    <TableBulkActions visible={!!selectedIds.length}>
      {showHpaV2 ? (
        <TableActionButton
          onClick={() =>
            openOptimizationChangeDialog({
              workloads: workloads.filter((w) => selectedIds.includes(w.id)),
              action: 'override',
              initialValue: null,
              title: 'Override optimization type',
              icon: <Icons.LinkBreak size={16} weight="bold" />,
              confirmBtnLabel: 'Apply override',
              onApply: ({ appliedType }) => {
                toggleOptimization({
                  optimizationType: appliedType,
                });
              },
            })
          }
          disabled={isWoopDemoMode}
          testId="batch-toggle-assign-workload-optimization-type"
        >
          Assign workload optimization type
        </TableActionButton>
      ) : (
        <>
          <TableActionButton
            onClick={() =>
              isConfirmationRequired
                ? openChangeOptimizationStatusOfMultipleWorkloadsConfirmationDialog(
                    {
                      onConfirm: () =>
                        toggleOptimization({
                          optimizationType: OptimizationType.VERTICAL,
                        }),
                      willBeEnabled: true,
                      workloadsCount: selectedIds.length,
                    }
                  )
                : toggleOptimization({
                    optimizationType: OptimizationType.VERTICAL,
                  })
            }
            disabled={isWoopDemoMode || onDisabled}
            testId="batch-toggle-vertical-scaling-on-btn"
          >
            Vertical optimization ON
          </TableActionButton>
          <TableActionButton
            onClick={() =>
              isConfirmationRequired
                ? openChangeOptimizationStatusOfMultipleWorkloadsConfirmationDialog(
                    {
                      onConfirm: () =>
                        toggleOptimization({
                          optimizationType: OptimizationType.OFF,
                        }),
                      willBeEnabled: false,
                      workloadsCount: selectedIds.length,
                    }
                  )
                : toggleOptimization({
                    optimizationType: OptimizationType.OFF,
                  })
            }
            disabled={isWoopDemoMode || offDisabled}
            testId="batch-toggle-vertical-scaling-off-btn"
          >
            Vertical optimization OFF
          </TableActionButton>
        </>
      )}
      <TableActionButton
        testId="batch-assign-vertical-scaling-policy-btn"
        disabled={isWoopDemoMode}
        onClick={() =>
          openAssignPolicyDrawer({
            workloadIds: selectedIds,
          })
        }
      >
        {showHpaV2 ? 'Assign scaling policy' : 'Assign vertical scaling policy'}
      </TableActionButton>
      <TableActionButton
        testId="batch-enable-auto-assignment-btn"
        disabled={isWoopDemoMode || !canEnableAutoAssignmentCount}
        onClick={enableBatchAutoAssignment}
      >
        Enable auto assignment
      </TableActionButton>
      {showPredictiveColumn && (
        <>
          <TableActionButton
            testId="batch-enable-predictive-scaling-btn"
            disabled={isWoopDemoMode || !canEnableWorkloadsCount}
            onClick={() =>
              enablePredictiveWorkloads({
                successMsg:
                  'Predictive scaling enabled for selected workloads.',
                errorMsg:
                  'Predictive scaling failed to enable for selected workloads.',
              })
            }
          >
            Enable predictive scaling
          </TableActionButton>
          <TableActionButton
            testId="batch-disable-predictive-scaling-btn"
            disabled={isWoopDemoMode || !canDisableEditableWorkloads}
            onClick={() =>
              disablePredictiveWorkloads({
                successMsg:
                  'Predictive scaling disabled for selected workloads.',
                errorMsg:
                  'Predictive scaling failed to disable for selected workloads.',
              })
            }
          >
            Disable predictive scaling
          </TableActionButton>
        </>
      )}
    </TableBulkActions>
  );
};
